<script lang="ts">
	//UI
	import AppLayout from "../v7/layout/AppLayout.svelte";
	import SalesItems, {
		type SalesItemsProps,
	} from "../v7/component/DailyReport/SalesItems.svelte";
	import TotalAmount, {
		type TotalAmountProps,
	} from "../v7/component/DailyReport/TotalAmount.svelte";
	import PaymentTypeReport, {
		type PaymentTypeReportProps,
		type PaymentInfo,
		defaultPaymentInfo,
	} from "../v7/component/DailyReport/PaymentTypeReport.svelte";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	//Utils
	import { format as dFormat } from "date-fns";
	import { ko } from "date-fns/locale";
	import { getDailyReport } from "../utils/api";
	//Types
	import type { TotalSaleByPaymentMethod } from "../utils/types";
	import { navigate } from "svelte-routing";
	//Props
	export let token: string | null = null;
	//Constants
	//Variables
	let loading = false;
	let reportDuration = "";

	let storename = "";
	let totalAmountProps: TotalAmountProps;
	let paymentTypeReportProps: PaymentTypeReportProps;
	let salesItemProps: SalesItemsProps;
	let salesOptionProps: SalesItemsProps;
	//Functions
	async function loadData(token: string) {
		try {
			if (token === null) throw new Error("Non Token");
			loading = true;
			const { data } = await getDailyReport(token);
			if (data.status !== "OK") throw new Error();
			const durationStart = format(new Date(data.reportFrom + "Z"), "EEEE p");
			const durationEnd = format(new Date(data.reportTo + "Z"), "EEEE p");
			const totalSalesAmount = data.totalSaleList.reduce(
				(acc, v) => acc + v.grandTotalPrice,
				0
			);
			const totalSalesCount = data.totalSalesCount;
			const totalDiscount = data.totalSaleList.reduce(
				(acc, v) => acc + v.totalDiscount,
				0
			);

			const cardReport = data.totalSaleByPaymentMethodList
				.filter((v) => v.paymentMethod === "INSTORE_CAT")
				.reduce(paymentTypeInfoReduceCallback, defaultPaymentInfo());
			const cashReport = data.totalSaleByPaymentMethodList
				.filter((v) => v.paymentMethod === "INSTORE_CASH")
				.reduce(paymentTypeInfoReduceCallback, defaultPaymentInfo());
			//
			const salesItems = data.itemQuantityRank
				.sort((a, b) => b.quantity - a.quantity)
				.map((v) => ({ name: v.itemname1 || v.itemname2, count: v.quantity }));
			const salesOptions = data.optionItemQuantityRank
				.sort((a, b) => b.quantity - a.quantity)
				.map((v) => ({
					name: `${v.itemname1} - ${v.itemname2}`,
					count: v.quantity,
				}));
			storename = data.storeName;
			reportDuration = `${durationStart} ~ ${durationEnd}`;
			totalAmountProps = {
				totalSalesAmount,
				totalSalesCount,
				totalDiscount,
			};
			paymentTypeReportProps = {
				card: cardReport,
				cash: cashReport,
			};
			salesItemProps = {
				items: salesItems,
				title: "판매 상품 내역",
			};
			salesOptionProps = {
				items: salesOptions,
				title: "판매 옵션 내역",
			};
		} catch (error) {
			console.log(error);
			navigate("/error");
		} finally {
			loading = false;
		}
	}

	function paymentTypeInfoReduceCallback(
		prev: PaymentInfo,
		cur: TotalSaleByPaymentMethod
	): PaymentInfo {
		prev.totalAmount += cur.totalReceivedPrice;
		prev.totalCount = prev.totalCount + 1;
		prev.totalReceivedTax += cur.totalReceivedTax;
		prev.totalTaxablePrice += cur.totalTaxablePrice;
		return prev;
	}

	function format(d: Date, s: string) {
		return dFormat(d, s, { locale: ko });
	}
	//Do
	$: loadData(token || "");

</script>

<AppLayout>
	<div slot="header" class="header">
		{#if !loading}
			<p class="title">{storename}</p>
			<p class="duration">{reportDuration}</p>
		{/if}
	</div>
	{#if loading}
		<ContainerLoading alwaysCentermode />
	{:else}
		<div class="body">
			{#if totalAmountProps && paymentTypeReportProps && salesItemProps && salesOptionProps}
				<TotalAmount {...totalAmountProps} />
				<PaymentTypeReport {...paymentTypeReportProps} />
				<SalesItems {...salesItemProps} />
				<SalesItems {...salesOptionProps} />
			{/if}
		</div>
	{/if}
</AppLayout>

<style lang="scss">
	.header {
		display: flex;
		width: 100%;
		justify-content: space-between;
		align-items: center;

		color: white;
		.title {
			font-weight: 500;
			font-size: 1.25rem;
		}
	}
	.body {
		width: 100%;
		box-sizing: border-box;
		padding: 20px;
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

</style>
